import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Page,
  Header,
  Content,
} from '@backstage/core-components';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  makeStyles,
  Tabs,
  Tab,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import AssessmentIcon from '@material-ui/icons/Assessment';
import { useApi } from '@backstage/core-plugin-api';
import { githubWorkflowsApiRef } from '@veecode-platform/github-workflows-common';
import { formatDistanceToNow } from 'date-fns';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLAYWRIGHT_REPORT_URL = 'https://silver-adventure-zg7o2ng.pages.github.io/';
const PLAYWRIGHT_REPO = 'exo-imaging/EW_Web_UI_Automation_Playwright';
const PLAYWRIGHT_HOSTNAME = 'github.com';
const PLAYWRIGHT_WORKFLOW_FILTER = 'playwright';
const PLAYWRIGHT_BRANCH = 'main';
const POLL_INTERVAL_MS = 20_000;
const INITIAL_POLL_DELAY_MS = 8_000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDuration = (createdAt: string, updatedAt: string): string => {
  const secs = Math.floor((new Date(updatedAt).getTime() - new Date(createdAt).getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return rem > 0 ? `${mins}m ${rem}s` : `${mins}m`;
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles(theme => ({
  tabBar: {
    marginBottom: 24,
    borderBottom: '1px solid rgba(255,255,255,0.12)',
  },
  // ── Run History tab ───────────────────────────────────────────────────────
  runHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap' as const,
  },
  runHeaderSpacer: { flex: 1 },
  runBtn: {
    textTransform: 'none' as const,
    fontWeight: 600,
  },
  openBtn: { textTransform: 'none' as const, whiteSpace: 'nowrap' as const },
  tableCard: {
    backgroundColor: '#1a1f2e',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
  },
  tableHead: {
    '& th': {
      backgroundColor: '#141824',
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      padding: theme.spacing(1, 2),
      whiteSpace: 'nowrap' as const,
    },
  },
  tableRow: {
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
    '& td': { padding: theme.spacing(1, 2), fontSize: '0.82rem' },
  },
  emptyCell: {
    textAlign: 'center' as const,
    color: theme.palette.text.secondary,
    padding: `${theme.spacing(4)}px !important`,
    fontSize: '0.85rem',
  },
  dispatchingBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.82rem',
    color: '#93c5fd',
  },
  muted: { color: theme.palette.text.secondary, fontSize: '0.78rem' },
  nowrap: { whiteSpace: 'nowrap' as const },
  // ── Report tab ─────────────────────────────────────────────────────────────
  reportCard: {
    padding: theme.spacing(4),
    backgroundColor: '#1a1f2e',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: theme.spacing(2),
    maxWidth: 480,
  },
  reportIcon: {
    fontSize: 56,
    color: theme.palette.primary.main,
    opacity: 0.8,
  },
  reportTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
  },
  reportSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    textAlign: 'center' as const,
  },
  reportOpenBtn: {
    textTransform: 'none' as const,
    fontWeight: 600,
    marginTop: theme.spacing(1),
  },
  latestRunBox: {
    width: '100%',
    padding: theme.spacing(1.5, 2),
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  // ── Dialog ─────────────────────────────────────────────────────────────────
  dialogField: {
    marginTop: theme.spacing(2),
  },
}));

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

const STATUS_MAP: Record<string, { bg: string; color: string; label: string }> = {
  success:     { bg: 'rgba(34,197,94,0.15)',   color: '#22c55e', label: 'Passed'    },
  failure:     { bg: 'rgba(239,68,68,0.15)',   color: '#ef4444', label: 'Failed'    },
  in_progress: { bg: 'rgba(251,191,36,0.15)',  color: '#fbbf24', label: 'Running'   },
  queued:      { bg: 'rgba(251,191,36,0.15)',  color: '#fbbf24', label: 'Queued'    },
  cancelled:   { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8', label: 'Cancelled' },
  skipped:     { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8', label: 'Skipped'   },
  unknown:     { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8', label: 'Unknown'   },
};

const RunStatusChip = ({ status, conclusion }: { status: string; conclusion: string | null }) => {
  const key = status === 'completed' ? (conclusion ?? 'unknown') : status;
  const s = STATUS_MAP[key] ?? STATUS_MAP.unknown;
  return (
    <Chip
      size="small"
      label={s.label}
      style={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.72rem', height: 22 }}
    />
  );
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RunRecord {
  id: number;
  run_number: number;
  status: string;
  conclusion: string | null;
  created_at: string;
  updated_at: string;
  html_url: string;
  actor: { login: string } | null;
}

interface WorkflowParam {
  name: string;
  description: string;
  default: string;
  required: boolean;
  type: string;
  options?: string[];
}

// ---------------------------------------------------------------------------
// Dispatch dialog
// ---------------------------------------------------------------------------

const DispatchDialog = ({
  open,
  onClose,
  params,
  workflowId,
  workflowsApi,
  onDispatched,
}: {
  open: boolean;
  onClose: () => void;
  params: WorkflowParam[];
  workflowId: number;
  workflowsApi: any;
  onDispatched: (dispatchedAt: number) => void;
}) => {
  const classes = useStyles();
  const [values, setValues] = useState<Record<string, string>>({});
  const [dispatching, setDispatching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Branch / tag selection
  const [refTab, setRefTab] = useState<'branch' | 'tag'>('branch');
  const [branches, setBranches] = useState<string[]>([]);
  const tags: string[] = [];
  const [selectedRef, setSelectedRef] = useState(PLAYWRIGHT_BRANCH);
  const [refSearch, setRefSearch] = useState('');
  const [loadingRefs, setLoadingRefs] = useState(false);

  useEffect(() => {
    if (!open) return;
    const init: Record<string, string> = {};
    params.forEach(p => { init[p.name] = p.default || ''; });
    setValues(init);
    setDispatching(false);
    setError(null);
    setRefTab('branch');
    setSelectedRef(PLAYWRIGHT_BRANCH);
    setRefSearch('');

    // Fetch branches
    setLoadingRefs(true);
    workflowsApi.listBranchesFromRepo(PLAYWRIGHT_HOSTNAME, PLAYWRIGHT_REPO)
      .then((list: any[]) => {
        const names: string[] = Array.isArray(list)
          ? list.map((b: any) => (typeof b === 'string' ? b : b?.name ?? '')).filter(Boolean)
          : [];
        setBranches(names.length ? names : [PLAYWRIGHT_BRANCH]);
      })
      .catch(() => setBranches([PLAYWRIGHT_BRANCH]))
      .finally(() => setLoadingRefs(false));
  }, [open, params, workflowsApi]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRefs = (refTab === 'branch' ? branches : tags).filter(r =>
    r.toLowerCase().includes(refSearch.toLowerCase()),
  );

  const handleDispatch = async () => {
    setDispatching(true);
    setError(null);
    const now = Date.now();
    try {
      await workflowsApi.startWorkflowRun(
        PLAYWRIGHT_HOSTNAME, PLAYWRIGHT_REPO, workflowId, selectedRef, values,
      );
      onDispatched(now);
      onClose();
    } catch (e: any) {
      setError(`Dispatch failed: ${e.message}`);
    } finally {
      setDispatching(false);
    }
  };

  const isValid = params.filter(p => p.required).every(p => (values[p.name] ?? '').trim() !== '');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Run Playwright Tests</DialogTitle>
      <DialogContent>
        {/* Branch / Tag selector */}
        <Box style={{ marginBottom: 8 }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Use workflow from
          </Typography>
          <Box style={{ border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, overflow: 'hidden' }}>
            {/* Ref type tabs */}
            <Box style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              {(['branch', 'tag'] as const).map(t => (
                <Box
                  key={t}
                  onClick={() => { setRefTab(t); setRefSearch(''); }}
                  style={{
                    padding: '6px 16px',
                    fontSize: '0.82rem',
                    fontWeight: refTab === t ? 700 : 400,
                    cursor: 'pointer',
                    borderBottom: refTab === t ? '2px solid #3b82f6' : '2px solid transparent',
                    color: refTab === t ? '#fff' : 'rgba(255,255,255,0.5)',
                    userSelect: 'none',
                    textTransform: 'capitalize',
                  }}
                >
                  {t === 'branch' ? 'Branches' : 'Tags'}
                </Box>
              ))}
            </Box>
            {/* Search */}
            <Box style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <input
                autoFocus
                placeholder={`Find a ${refTab}…`}
                value={refSearch}
                onChange={e => setRefSearch(e.target.value)}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 4,
                  padding: '4px 8px',
                  color: '#fff',
                  fontSize: '0.82rem',
                  outline: 'none',
                }}
              />
            </Box>
            {/* Ref list */}
            <Box style={{ maxHeight: 160, overflowY: 'auto' }}>
              {loadingRefs ? (
                <Box style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                  <CircularProgress size={18} />
                </Box>
              ) : refTab === 'tag' && tags.length === 0 ? (
                <Box style={{ padding: '12px 16px', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                  No tags found
                </Box>
              ) : filteredRefs.length === 0 ? (
                <Box style={{ padding: '12px 16px', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>
                  No matches
                </Box>
              ) : (
                filteredRefs.map(ref => (
                  <Box
                    key={ref}
                    onClick={() => setSelectedRef(ref)}
                    style={{
                      padding: '7px 16px',
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      backgroundColor: selectedRef === ref ? 'rgba(59,130,246,0.15)' : 'transparent',
                      color: selectedRef === ref ? '#93c5fd' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    {selectedRef === ref && <span style={{ color: '#3b82f6', fontWeight: 700 }}>✓</span>}
                    {ref}
                  </Box>
                ))
              )}
            </Box>
          </Box>
          <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
            Running from: <strong>{selectedRef}</strong>
          </Typography>
        </Box>

        {/* Workflow inputs */}
        {params.map(p => (
          <Box key={p.name} className={classes.dialogField}>
            {p.options && p.options.length > 0 ? (
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>{p.description || p.name}{p.required ? ' *' : ''}</InputLabel>
                <Select
                  value={values[p.name] ?? ''}
                  onChange={e => setValues(prev => ({ ...prev, [p.name]: String(e.target.value) }))}
                  label={`${p.description || p.name}${p.required ? ' *' : ''}`}
                >
                  {p.options.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
                {p.description && <FormHelperText>{p.description}</FormHelperText>}
              </FormControl>
            ) : (
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={`${p.description || p.name}${p.required ? ' *' : ''}`}
                value={values[p.name] ?? ''}
                onChange={e => setValues(prev => ({ ...prev, [p.name]: e.target.value }))}
                required={p.required}
              />
            )}
          </Box>
        ))}
        {error && (
          <Typography variant="caption" style={{ color: '#ef4444', display: 'block', marginTop: 12 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={dispatching}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDispatch}
          disabled={dispatching || !isValid}
          startIcon={dispatching ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
        >
          {dispatching ? 'Dispatching…' : 'Run workflow'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Tab 1 — Test Report
// ---------------------------------------------------------------------------

const TestReportTab = ({ latestRun }: { latestRun: RunRecord | null }) => {
  const classes = useStyles();
  return (
    <Box display="flex" justifyContent="center" pt={4}>
      <Box className={classes.reportCard}>
        <AssessmentIcon className={classes.reportIcon} />
        <Typography className={classes.reportTitle}>EW Nexgen Playwright E2E Test Suite</Typography>
        <Typography className={classes.reportSubtitle}>
          The test report is hosted on GitHub Pages. Click below to open the latest results in a new tab.
        </Typography>

        {latestRun && (
          <Box className={classes.latestRunBox}>
            <Typography variant="caption" color="textSecondary" style={{ flex: 1 }}>
              Latest run #{latestRun.run_number} &nbsp;·&nbsp;{' '}
              {formatDistanceToNow(new Date(latestRun.created_at), { addSuffix: true })}
              {latestRun.actor ? ` · by ${latestRun.actor.login}` : ''}
            </Typography>
            <RunStatusChip status={latestRun.status} conclusion={latestRun.conclusion} />
            <Tooltip title="View run on GitHub">
              <IconButton
                size="small"
                href={latestRun.html_url}
                target="_blank"
                rel="noopener noreferrer"
                component="a"
              >
                <OpenInNewIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          className={classes.reportOpenBtn}
          startIcon={<OpenInNewIcon />}
          href={PLAYWRIGHT_REPORT_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open Test Report
        </Button>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Tab 2 — Run Tests
// ---------------------------------------------------------------------------

const RunTestsTab = () => {
  const classes = useStyles();
  const workflowsApi = useApi(githubWorkflowsApiRef) as any;

  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [workflowId, setWorkflowId] = useState<number | null>(null);
  const [workflowParams, setWorkflowParams] = useState<WorkflowParam[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingParams, setLoadingParams] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dispatchedAt, setDispatchedAt] = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRuns = useCallback(async () => {
    try {
      const data = await workflowsApi.listRunHistory(
        PLAYWRIGHT_HOSTNAME, PLAYWRIGHT_REPO, PLAYWRIGHT_WORKFLOW_FILTER, 20,
      );
      if (data?.workflow?.id) setWorkflowId(data.workflow.id);
      setRuns(data?.runs ?? []);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [workflowsApi]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  // Stop polling once dispatched run completes
  useEffect(() => {
    if (!dispatchedAt) return;
    const done = runs.some(
      r => new Date(r.created_at).getTime() >= dispatchedAt - 90_000 && r.status === 'completed',
    );
    if (done) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      setDispatchedAt(null);
    }
  }, [runs, dispatchedAt]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleOpenDialog = async () => {
    if (!workflowParams.length) {
      setLoadingParams(true);
      try {
        const workflows: any[] = await workflowsApi.listWorkflows(
          PLAYWRIGHT_HOSTNAME, PLAYWRIGHT_REPO, PLAYWRIGHT_BRANCH, [PLAYWRIGHT_WORKFLOW_FILTER],
        );
        const wf = workflows.find((w: any) => w.workflow?.id && w.parameters);
        if (wf) {
          if (!workflowId) setWorkflowId(wf.workflow.id);
          setWorkflowParams(wf.parameters ?? []);
        }
      } catch {
        // Fall back to opening the dialog with no params if fetch fails
      } finally {
        setLoadingParams(false);
      }
    }
    setDialogOpen(true);
  };

  const handleDispatched = (ts: number) => {
    setDispatchedAt(ts);
    if (pollRef.current) clearInterval(pollRef.current);
    setTimeout(async () => {
      await fetchRuns();
      pollRef.current = setInterval(fetchRuns, POLL_INTERVAL_MS);
    }, INITIAL_POLL_DELAY_MS);
  };

  const isRunning = runs.some(r => r.status === 'in_progress' || r.status === 'queued');

  return (
    <>
      {/* Header */}
      <Box className={classes.runHeader}>
        <Typography variant="h6" style={{ fontWeight: 600, fontSize: '0.95rem' }}>
          Playwright Tests — Run History
        </Typography>
        <Box className={classes.runHeaderSpacer} />
        <IconButton size="small" onClick={fetchRuns} title="Refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
        <Button
          size="small"
          variant="outlined"
          className={classes.openBtn}
          startIcon={<OpenInNewIcon />}
          href={`https://github.com/${PLAYWRIGHT_REPO}/actions/workflows/playwright.yml`}
          target="_blank"
          rel="noopener noreferrer"
        >
          GitHub Actions
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={classes.runBtn}
          startIcon={loadingParams ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
          onClick={handleOpenDialog}
          disabled={loadingParams}
        >
          Run Tests
        </Button>
      </Box>

      {/* In-flight indicator */}
      {(isRunning || dispatchedAt) && (
        <Box className={classes.dispatchingBox}>
          <CircularProgress size={12} style={{ color: '#93c5fd' }} />
          <span>Test run in progress — auto-refreshing every 20s…</span>
        </Box>
      )}

      {error && (
        <Typography variant="caption" style={{ color: '#ef4444', display: 'block', marginBottom: 12 }}>
          {error}
        </Typography>
      )}

      {/* Run history table */}
      <Paper className={classes.tableCard} elevation={0}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Triggered by</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className={classes.emptyCell}>No runs found</TableCell>
                </TableRow>
              ) : (
                runs.map(run => (
                  <TableRow key={run.id} className={classes.tableRow}>
                    <TableCell>
                      <span className={classes.muted}>#{run.run_number}</span>
                    </TableCell>
                    <TableCell>
                      <RunStatusChip status={run.status} conclusion={run.conclusion} />
                    </TableCell>
                    <TableCell>
                      <span className={classes.muted}>{run.actor?.login ?? '—'}</span>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={new Date(run.created_at).toLocaleString()}>
                        <span className={`${classes.muted} ${classes.nowrap}`}>
                          {formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <span className={`${classes.muted} ${classes.nowrap}`}>
                        {run.status === 'completed' ? formatDuration(run.created_at, run.updated_at) : '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View on GitHub">
                        <IconButton
                          size="small"
                          href={run.html_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          component="a"
                        >
                          <OpenInNewIcon style={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Dispatch dialog */}
      {workflowId && (
        <DispatchDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          params={workflowParams}
          workflowId={workflowId}
          workflowsApi={workflowsApi}
          onDispatched={handleDispatched}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const TestingPage = () => {
  const [tab, setTab] = useState(0);
  const workflowsApi = useApi(githubWorkflowsApiRef) as any;

  // Fetch latest run for the report tab summary
  const [latestRun, setLatestRun] = useState<RunRecord | null>(null);
  useEffect(() => {
    workflowsApi
      .listRunHistory(PLAYWRIGHT_HOSTNAME, PLAYWRIGHT_REPO, PLAYWRIGHT_WORKFLOW_FILTER, 1)
      .then((d: any) => setLatestRun(d?.runs?.[0] ?? null))
      .catch(() => {});
  }, [workflowsApi]);

  return (
    <Page themeId="tool">
      <Header title="Testing" subtitle="E2E test reports and quality metrics" />
      <Content>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 24, borderBottom: '1px solid rgba(255,255,255,0.12)' }}
        >
          <Tab label="EW Nexgen Playwright E2E Test Suite" />
          <Tab label="Run Tests" />
        </Tabs>
        {tab === 0 && <TestReportTab latestRun={latestRun} />}
        {tab === 1 && <RunTestsTab />}
      </Content>
    </Page>
  );
};
